/*
 * eu-seller/labels.js — the thin wrapper between our label content and the
 * label-maker engine (public/js/label-maker/*), which is NOT changed here.
 *
 * What label-maker already does and we only call:
 *   - formats: A4/Letter sheets (LabelSheets.all) and thermal rolls (rolls, roll);
 *   - text: LabelLayout.layoutLabel with the canvas measurer of LabelRender,
 *     the same line breaks on screen and in the PDF;
 *   - preview: LabelRender.renderLabel / renderSheet (SVG in millimetres);
 *   - PDF: LabelPdf.build (pdf-lib + PdfTextFont), test label for rolls.
 *
 * What it does not know and this file adds on top: VECTOR SYMBOLS (CE,
 * Triman, the bin, CLP pictograms…). label-maker items are text, barcode,
 * image and shape; a symbol drawn as an image would be a raster. So:
 *   - preview: after label-maker has drawn the SVG, a <g> per symbol is
 *     appended at the same millimetre box;
 *   - PDF: after LabelPdf.build has saved the document, it is reopened with
 *     pdf-lib and each symbol is drawn on its cell through window.__pdfSvgPath
 *     (the site's door for long paths), at the cell position LabelSheets.cellAt
 *     gives — the same function label-maker placed the text with.
 * Both sides read one set of numbers (EuSymbols.layers + the box), so preview
 * and print agree by construction, as label-maker's own two backends do.
 */
(function (root) {
    'use strict';

    var PT = 72 / 25.4;
    var FACE = "'LM Noto Sans', 'Noto Sans', sans-serif";
    var familyFor = function () { return FACE; };
    var NS = 'http://www.w3.org/2000/svg';
    var _measure = null;

    function measure() {
        if (!_measure) _measure = root.LabelRender.measurer(familyFor);
        return _measure;
    }
    function fontsReady() {
        return root.LabelRender.fontsReady(['"LM Noto Sans"']).then(function () { _measure = null; });
    }

    /* ── formats ─────────────────────────────────────────────────────────── */
    function sheetFormats() {
        return root.LabelSheets.all().filter(function (f) { return f.shape !== 'circle' && f.family !== 'derived'; });
    }
    function rollFormats() { return root.LabelSheets.rolls(); }
    function formatById(id, custom) {
        if (id === 'roll-custom') return root.LabelSheets.roll(custom || {});
        return root.LabelSheets.byId(id) || null;
    }
    function isRoll(fmt) { return root.LabelSheets.isRoll(fmt); }
    function formatLabel(fmt, t) {
        var S = root.LabelSheets;
        var size = S.fmt(fmt.w, 1) + ' × ' + S.fmt(fmt.h, 1) + ' mm';
        if (isRoll(fmt)) return size;
        var page = fmt.page === 'letter' ? 'Letter' : 'A4';
        var n = fmt.cols * fmt.rows;
        return size + ' · ' + page + ' × ' + n + (fmt.part ? ' · Avery ' + fmt.part : '');
    }

    /* ── one label's content ─────────────────────────────────────────────── */
    /* blocks → stack (EuCompose, in mm) → label-maker items + our symbols. */
    function layout(blocks, fmt, opts) {
        opts = opts || {};
        var label = { w: fmt.w, h: fmt.h };
        var st = root.EuCompose.stack(blocks, label, {
            measure: measure(), xh: opts.xh, capacityL: opts.capacityL, base: opts.base,
        });
        var lay = root.LabelLayout.layoutLabel(label, st.elements, { measure: measure() });
        return { items: lay.items, issues: lay.issues, symbols: st.symbols, stack: st };
    }

    /* ── symbols into an SVG ─────────────────────────────────────────────── */
    function svgEl(name, attrs) {
        var n = document.createElementNS(NS, name);
        for (var k in attrs) if (attrs[k] != null) n.setAttribute(k, String(attrs[k]));
        return n;
    }
    function symbolGroup(sym, opts) {
        var S = root.EuSymbols;
        var def = S.SYMBOLS[sym.id];
        var k = sym.w / def.vb[0];
        var g = svgEl('g', { transform: 'translate(' + sym.x + ' ' + sym.y + ') scale(' + k + ')', 'data-es-symbol': sym.id });
        S.layers(sym.id, opts).forEach(function (L) {
            g.appendChild(svgEl('path', {
                d: L.d, fill: L.fill || 'none', 'fill-rule': L.rule === 'evenodd' ? 'evenodd' : null,
                stroke: L.stroke || null, 'stroke-width': L.stroke ? L.sw : null, 'stroke-linejoin': L.stroke ? 'round' : null,
            }));
        });
        return g;
    }
    function renderOne(host, fmt, content, opts) {
        opts = opts || {};
        var svg = root.LabelRender.renderLabel(host, fmt, content, { fontFamilyFor: familyFor, showSafe: opts.showSafe !== false, safe: 1.5 });
        (content.symbols || []).forEach(function (s) { svg.appendChild(symbolGroup(s, s.opts || opts.symbolOpts)); });
        return svg;
    }
    function renderSheet(host, fmt, cells, opts) {
        opts = opts || {};
        var S = root.LabelSheets;
        var svg = root.LabelRender.renderSheet(host, fmt, cells, { fontFamilyFor: familyFor, ruler: !isRoll(fmt), showNumbers: false });
        var per = S.perSheet(fmt);
        for (var i = 0; i < per && i < cells.length; i++) {
            var c = cells[i];
            if (!c || !c.symbols || !c.symbols.length) continue;
            var cell = S.cellAt(fmt, i, {});
            var g = svgEl('g', { transform: 'translate(' + cell.x + ' ' + cell.y + ')' });
            c.symbols.forEach(function (s) { g.appendChild(symbolGroup(s, s.opts || opts.symbolOpts)); });
            svg.appendChild(g);
        }
        return svg;
    }

    /* ── PDF ─────────────────────────────────────────────────────────────── */
    function hexRgb(PL, hex) {
        var h = String(hex || '#000').replace('#', '');
        if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        var n = parseInt(h, 16);
        return PL.rgb(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
    }
    function drawSymbol(PL, page, pageHmm, x, y, sym, k0, opts) {
        var S = root.EuSymbols;
        var def = S.SYMBOLS[sym.id];
        var k = sym.w / def.vb[0] * k0;
        S.layers(sym.id, opts).forEach(function (L) {
            var o = { x: x * PT, y: (pageHmm - y) * PT, scale: k * PT };
            if (L.fill) o.color = hexRgb(PL, L.fill);
            if (L.stroke) { o.borderColor = hexRgb(PL, L.stroke); o.borderWidth = L.sw; o.borderLineCap = PL.LineCapStyle ? PL.LineCapStyle.Round : undefined; }
            else o.borderWidth = 0;
            if (L.rule === 'evenodd') o.fillRule = 'evenodd';
            if (!L.fill && !L.stroke) return;
            // The site's door (timbrica-core.js) — no direct pdf-lib path call here.
            root.__pdfSvgPath(page, L.d, o, PL);
        });
    }

    /* job = { fmt, cells:[content|null], title, lang, rulerCaption, correction }
     * → Promise<{blob, bytes, pages}>. */
    function pdf(job) {
        var S = root.LabelSheets;
        var fmt = job.fmt;
        var roll = isRoll(fmt);
        return root.LabelPdf.build({
            fmt: fmt, cells: job.cells, order: 'row', correction: job.correction || null,
            outline: false, cutMarks: false, ruler: !roll, rulerCaption: job.rulerCaption || '50 mm',
        }).then(function (res) {
            var PL = root.PDFLib;
            return PL.PDFDocument.load(res.bytes).then(function (doc) {
                var pages = doc.getPages();
                var per = S.perSheet(fmt);
                var pageMm = S.page(fmt.page);
                var corr = S.correction(job.correction);
                job.cells.forEach(function (c, idx) {
                    if (!c || !c.symbols || !c.symbols.length) return;
                    var page = pages[Math.floor(idx / per)];
                    if (!page) return;
                    var cell = S.cellAt(fmt, idx % per, { order: 'row', correction: job.correction });
                    c.symbols.forEach(function (s) {
                        drawSymbol(PL, page, pageMm.h, cell.x + s.x * corr.scale, cell.y + s.y * corr.scale, s, corr.scale, s.opts || job.symbolOpts);
                    });
                });
                if (job.title) doc.setTitle(job.title);
                if (job.lang && typeof doc.setLanguage === 'function') doc.setLanguage(job.lang);
                doc.setCreator('Timbrica eu-seller-label');
                doc.setProducer('Timbrica');
                return doc.save({ useObjectStreams: true }).then(function (bytes) {
                    return { blob: new Blob([bytes], { type: 'application/pdf' }), bytes: bytes, pages: pages.length };
                });
            });
        });
    }

    /* Standalone SVG of the symbols this product uses, for a designer. */
    function symbolsSvg(ids, opts) {
        var S = root.EuSymbols;
        var x = 0, gap = 4, h = 20, parts = [];
        ids.forEach(function (id) {
            var def = S.SYMBOLS[id];
            if (!def) return;
            var w = h * def.vb[0] / def.vb[1];
            var inner = S.svg(id, opts).replace(/^<svg[^>]*>/, '').replace(/<\/svg>$/, '');
            parts.push('<g transform="translate(' + x + ' 0) scale(' + (h / def.vb[1]) + ')">' + inner + '</g>');
            x += w + gap;
        });
        var W = Math.max(1, x - gap);
        return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + W + ' ' + h + '" width="' + W + 'mm" height="' + h + 'mm">' + parts.join('') + '</svg>';
    }

    root.EuLabels = {
        fontsReady: fontsReady, measure: measure, familyFor: familyFor,
        sheetFormats: sheetFormats, rollFormats: rollFormats, formatById: formatById, isRoll: isRoll, formatLabel: formatLabel,
        layout: layout, renderOne: renderOne, renderSheet: renderSheet, pdf: pdf, symbolsSvg: symbolsSvg,
        __drawSymbol: drawSymbol,
    };
})(typeof window !== 'undefined' ? window : this);
